/**
 * My Alphabet — the long-term collection screen.
 *
 * Eva earns a letter the first time she masters a word starting with it
 * (backend: migrations/0006 + applyMasteryUpdate). The full a–z board is
 * always shown; locked letters are dimmed so the goal ("collect them
 * all") is visible — rare letters (q/x/z) are the long tail.
 *
 * Mirrors MyStickersScreen's load + mark-seen pattern: opening the board
 * clears the "new!" flag on freshly-earned letters.
 */
import { useEffect, useState } from "react";

import { Button } from "@/components/Button";
import { StickerEmblem } from "@/components/StickerEmblem";
import { useNavigation } from "@/context/NavigationContext";
import { api } from "@/lib/api";
import type { AlphabetLetter } from "@/types/data";
import "./AlphabetScreen.css";

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; letters: AlphabetLetter[] };

export function AlphabetScreen() {
  const { navigate } = useNavigation();
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await api.listAchievements();
      if (cancelled) return;
      if (res.status !== 200 || !res.body) {
        setState({ kind: "error", message: "Couldn't load your alphabet." });
        return;
      }
      setState({ kind: "ready", letters: res.body.alphabet });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Mark freshly-earned letters seen on view — same one-shot pattern as
  // My Stickers (keyed on state.kind so the optimistic flip doesn't loop).
  useEffect(() => {
    if (state.kind !== "ready") return;
    const unseen = state.letters
      .filter((l) => l.unlockedAt !== null && !l.seenByUser)
      .map((l) => l.letter);
    if (unseen.length === 0) return;
    void api.markAchievementsSeen({ alphabetLetters: unseen });
    setState((s) =>
      s.kind === "ready"
        ? {
            ...s,
            letters: s.letters.map((l) =>
              unseen.includes(l.letter) ? { ...l, seenByUser: true } : l,
            ),
          }
        : s,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.kind]);

  if (state.kind === "loading") {
    return (
      <main className="alphabet">
        <p className="alphabet__loading">Loading your alphabet…</p>
      </main>
    );
  }
  if (state.kind === "error") {
    return (
      <main className="alphabet">
        <p className="alphabet__error">{state.message}</p>
        <Button onClick={() => navigate("home")}>Back to home</Button>
      </main>
    );
  }

  const total = state.letters.length;
  const earned = state.letters.filter((l) => l.unlockedAt !== null).length;
  const pct = total > 0 ? Math.round((earned / total) * 100) : 0;

  return (
    <main className="alphabet">
      <header className="alphabet__header">
        <h1 className="alphabet__title">My Alphabet</h1>
        <Button size="small" variant="secondary" onClick={() => navigate("home")}>
          Back
        </Button>
      </header>

      <p className="alphabet__intro">Master a word to win its first letter. Collect all 26!</p>

      <div className="alphabet__progress">
        <div className="alphabet__progress-head">
          <span>
            <span className="alphabet__count">{earned}</span>
            <span className="alphabet__count-label"> of {total} letters</span>
          </span>
          <span className="alphabet__pct">{pct}%</span>
        </div>
        <div
          className="alphabet__bar"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Alphabet collected"
        >
          <div className="alphabet__bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="alphabet__grid">
        {state.letters.map((l) => (
          <div className="alphabet__cell" key={l.letter}>
            <StickerEmblem
              category="alphabet"
              stickerId={`letter_${l.letter}`}
              name={l.letter.toUpperCase()}
              locked={l.unlockedAt === null}
              isNew={l.unlockedAt !== null && !l.seenByUser}
            />
          </div>
        ))}
      </div>
    </main>
  );
}
